import React, { cloneElement, Component, Fragment } from 'react';
import { bool, number, string, func, shape, any, arrayOf, element } from 'prop-types';
import { get } from 'lodash';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import Chip from '@material-ui/core/Chip';

import styles from './styles.scss';
import classnames from 'classnames';

export const DEFAULT_MESSAGE = `Connect your account data in order to kick off attribution reporting.`;
export const INPUT_ERROR_MESSAGE = 'Email list cannot be empty';
export const EMPTY_MESSAGE_ERROR = 'Message cannot be blank';
export const EMAIL_LIMIT = 5;
export const DEFAULT_EMAIL_INFO = {
  emails: [],
  message: DEFAULT_MESSAGE,
  emailError: '',
  emailValue: '',
  msgError: ''
};

const CHARACTER_LIMIT = 750;

export default class AdvertiserEmailInfo extends Component {
  static propTypes = {
    edit: bool,
    disabled: bool,
    emailLimit: number,
    inviteRecipient: string,
    inviteMessage: string,
    emailInputLabel: string,
    messageInputLabel: string,
    messageCharacterLimit: number,
    onEmailInfoChange: func,
    classes: shape({ any }),
    emailInfo: shape({
      emails: arrayOf(string),
      emailError: string,
      emailValue: string,
      message: string,
      msgError: string
    }),
    chipTemplate: element.isRequired
  };

  static defaultProps = {
    emailInputLabel: 'Email*',
    messageInputLabel: 'Message*',
    messageCharacterLimit: CHARACTER_LIMIT,
    emailLimit: EMAIL_LIMIT,
    disabled: false,
    chipTemplate: <Chip/>
  };

  state = {
    remainingCharacters: CHARACTER_LIMIT
  };

  static getDerivedStateFromProps(props, prevState) {
    const { edit, emailInfo, inviteRecipient, inviteRecipients, inviteMessage } = props;
    const { emailInfo: prevEmailInfo } = prevState;

    if (inviteRecipient || get(inviteRecipients, 'length') > 0) {
      const emailValue = get(emailInfo, 'emailValue', '');
      const emailError = get(emailInfo, 'emailError', '');
      const msgError = get(emailInfo, 'msgError', '');
      const emails = inviteRecipient ? get(emailInfo, 'emails', [inviteRecipient]) : get(emailInfo, 'emails', inviteRecipients);
      const message = get(emailInfo, 'message', inviteMessage);

      return {
        emailInfo: {
          emails,
          message,
          emailError,
          msgError,
          emailValue
        }
      }
    }

    if (emailInfo) {
      return { emailInfo };
    }

    if (!edit && prevEmailInfo) {
      return { emailInfo: prevEmailInfo };
    }

    return { emailInfo: DEFAULT_EMAIL_INFO };
  }

  componentWillUnmount() {
    this.updateEmailInfo(DEFAULT_EMAIL_INFO);
  }

  updateEmailInfo(emailInfo) {
    const { onEmailInfoChange } = this.props;
    onEmailInfoChange && onEmailInfoChange(emailInfo);
  }

  handleEmailChange = () => {
    const emailValue = this.emailInputRef.current.value;
    const { emailInfo } = this.state;
    const newEmailInfo = { ...emailInfo, emailValue, emailError: '' };

    this.updateEmailInfo(newEmailInfo);
    this.setState({ emailInfo: newEmailInfo });
  };

  handleEmailKeyDown = (event) => {
    if (['Enter', 'Tab', ',', ' '].includes(event.key)) {
      const { emailInfo } = this.state;
      const { emailValue, emails } = emailInfo;
      event.preventDefault();

      const email = emailValue.trim();

      if (!email && event.key === 'Tab') {
        this.messageTextFieldRef.current.focus();
      }

      if (email && this.isValidEmail(email)) {
        const updatedEmailInfo = { ...emailInfo, emails: [...emails, email], emailError: '', emailValue: '' };

        this.setState({ emailInfo: updatedEmailInfo });
        this.updateEmailInfo(updatedEmailInfo);
      }
    }
  };

  handleEmailDelete = (emailToDelete) => {
    const { emailInfo: prevEmailInfo } = this.state;
    const { emails } = prevEmailInfo;

    const filteredEmails = emails.filter(email => email !== emailToDelete);
    const emailInfo = { ...prevEmailInfo, emails: filteredEmails };

    if (filteredEmails.length === 0) {
      emailInfo.emailError = INPUT_ERROR_MESSAGE;
    }

    this.setState({ emailInfo });
    this.updateEmailInfo(emailInfo);
    this.handleEmailInputFocus();
  };

  handleMessageChange = (event) => {
    const { messageCharacterLimit } = this.props;
    const { emailInfo } = this.state;
    const message = event.target.value;
    const remainingCharacters = messageCharacterLimit - get(message, 'length', 0);
    const updatedEmailInfo = { ...emailInfo, message, msgError: '' };
    this.setState({ emailInfo: updatedEmailInfo, remainingCharacters });
    this.updateEmailInfo(updatedEmailInfo);
  };

  isValidEmail(email) {
    const { emailLimit } = this.props;
    const { emailInfo } = this.state;
    let emailError = null;

    if (!this.isEmail(email)) {
      emailError = `${email} is not a valid email address`;
    }

    if (this.isEmailInList(email)) {
      emailError = `${email} is already in the list`;
    }

    if (this.isEmailListOverLimit()) {
      emailError = `Only ${emailLimit} emails are allowed`;
    }

    if (emailError) {
      const updatedEmailInfo = { ...emailInfo, emailError };

      this.setState({ emailInfo: updatedEmailInfo });
      this.updateEmailInfo(updatedEmailInfo);

      return false;
    }

    return true;
  }

  isEmail(email) {
    const re = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return re.test(String(email).toLowerCase());
  }

  isEmailInList(email) {
    const { emailInfo } = this.state;
    const { emails } = emailInfo;

    return !!emails.find(emailInList => emailInList.toLowerCase() === email.toLowerCase());
  }

  isEmailListOverLimit() {
    const { emailLimit } = this.props;
    const { emailInfo } = this.state;
    const { emails } = emailInfo;

    return emails.length >= emailLimit;
  }

  handleEmailInputFocus = () => {
    this.emailInputRef.current.focus();
  };

  handleMessageTextFieldFocus = (event) => {
    // Move caret at the end of the input
    const value = event.target.value;
    event.target.value = '';
    event.target.value = value;
  };

  //---------------------------------------------------------------------
  //---------------------------Draw Functions----------------------------
  //---------------------------------------------------------------------
  drawDeleteIcon = () => {
    return (
      <div data-test="emailDeleteIcon" className={styles.deleteIcon}>
        <CloseIcon fontSize="inherit"/>
      </div>
    );
  };

  emailInputRef = React.createRef();
  messageTextFieldRef = React.createRef();
  drawEmailInput() {
    const { disabled, chipTemplate } = this.props;
    const { emailInfo } = this.state;
    const { emailValue, emails, emailError } = emailInfo;
    const deleteIcon = this.drawDeleteIcon();

    const inputWidth = (emails.length > 0) ? `${(emailValue.length + 1) * 10}px` : '100%';

    return (
      <div data-test="advEmailInfo" className={styles.emailInput} onClick={this.handleEmailInputFocus}>
        <Fragment>
          {
            emails.map(email => {
              return cloneElement(chipTemplate, {
                key: email,
                label: email,
                disabled,
                deleteIcon: deleteIcon,
                onDelete: disabled ? null : () => this.handleEmailDelete(email)
              });
            })
          }
        </Fragment>
        <input
          data-test="advFormEmailInput"
          type="text"
          ref={this.emailInputRef}
          style={{ width: inputWidth }}
          error={emailError}
          disabled={disabled}
          onChange={this.handleEmailChange}
          onKeyDown={this.handleEmailKeyDown}
          value={emailValue || ''}
          required
        />
      </div>
    );
  }

  render() {
    const { disabled, emailInputLabel, messageInputLabel, messageCharacterLimit, classes } = this.props;
    const { emailInfo } = this.state;
    const {
      emailError,
      message,
      msgError
    } = emailInfo;
    const remainingCharacters = messageCharacterLimit - get(message, 'length', 0);
    const remainingCharacterLabel = `${remainingCharacters} of ${messageCharacterLimit} left`;

    return (
      <div className={classnames(classes, styles.advertiserEmailInfo)}>
        <div className={classnames(styles.invitationLabel, { [styles.labelError]: !!emailError })}>{emailInputLabel}</div>
        {this.drawEmailInput()}
        {emailError && <p data-test="advEmailError" className={styles.error}>{emailError}</p>}
        <div className={styles.messageContainer}>
          <div className={styles.invitationLabel}>{messageInputLabel}</div>
          <TextField
            data-test="advEmailMsg"
            inputRef={this.messageTextFieldRef}
            disabled={disabled}
            className={styles.messageTextField}
            name='message'
            margin='normal'
            rows='9'
            value={message}
            multiline
            fullWidth
            InputProps={{ disableUnderline: true }} // TODO: Investigate why they don't work together
            inputProps={{ maxLength: messageCharacterLimit }}
            onChange={this.handleMessageChange}
            onFocus={this.handleMessageTextFieldFocus}
          />
          {!disabled && msgError && <p className={styles.error}>{msgError}</p>}
          <div className={styles.characterCounter}>{remainingCharacterLabel}</div>
        </div>
      </div>
    );
  }
}
